import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { InicioPage } from './pages/InicioPage';
import NosotrosPage from './pages/NosotrosPage';
import EventosPage from './pages/EventosPage';
import IglesiaPage from './pages/IglesiaPage';
import ContactoPage from './pages/ContactoPage';
import DonacionesPage from './pages/DonacionesPage';
import FundacionPage from './pages/FundacionPage';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import MembresíasPage from './pages/MembresíasPage';
import UsuariosPage from './pages/UsuariosPage';
import { RolesPage } from './pages/security/RolesPage';
import { ModulesPage } from './pages/security/ModulesPage';
import { PermissionsPage } from './pages/security/PermissionsPage';
import { MinisteriosPage } from './pages/iglesia/MinisteriosPage';
import DashboardEventosPage from './pages/DashboardEventosPage';
import ContenidoPublicoPage from './pages/configuracion/ContenidoPublicoPage';
import DashboardContactosPage from './pages/iglesia/DashboardContactosPage';
import ProgramacionSemanalPage from './pages/iglesia/ProgramacionSemanalPage';
import ChatPage from './pages/ChatPage';
import MemberDashboardPage from './pages/MemberDashboardPage';
const MayordomiaPage = lazy(() => import('./pages/mayordomia/MayordomiaPage'));
import Layout from './components/layout/Layout';
import { DashboardLayout } from './components/layout/DashboardLayout';
import { usePermissions } from './hooks/usePermissions';
import { useAuthStore } from './store/auth.store';

// Crear cliente de React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

// Helper para verificar si el usuario está autenticado
const isAuthenticated = () => {
  const token = localStorage.getItem('accessToken');
  const userData = localStorage.getItem('userData');
  return !!(token && userData);
};

// Determina la ruta inicial según los permisos del usuario guardado
const getInitialRoute = (): string => {
  try {
    const userData = localStorage.getItem('userData');
    if (!userData) return '/login';
    const user = JSON.parse(userData);

    const hasModuleAccess = (moduleName: string) =>
      user?.roles?.some((rol: any) =>
        rol.modulos?.some((modulo: any) =>
          modulo.nombre === moduleName && modulo.permisos?.some((p: any) => p.nombre === 'ver')
        )
      );

    if (hasModuleAccess('Dashboard')) return '/dashboard';
    if (hasModuleAccess('Mi Perfil')) return '/dashboard/miembro';
    return '/';
  } catch {
    return '/login';
  }
};

// Componente para proteger rutas públicas (redirigir a dashboard si está logueado)
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  return isAuthenticated() ? <Navigate to={getInitialRoute()} replace /> : <>{children}</>;
};

// Componente para proteger rutas privadas (redirigir a login si no está logueado)
const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  return isAuthenticated() ? <>{children}</> : <Navigate to="/login" replace />;
};

// Componente para proteger rutas por permisos de módulo
const PermissionRoute = ({ children }: { children: React.ReactNode }) => {
  const { hasPermission } = usePermissions();
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const loadUser = useAuthStore((state) => state.loadUser);

  // Al recargar, DashboardLayout hidrata el store desde localStorage para
  // pintar de inmediato; aquí revalidamos SIEMPRE contra el API en segundo
  // plano, así nunca operamos con datos de sesión viejos (módulos/permisos).
  // Si la sesión es inválida, el interceptor del HTTP client redirige a login.
  useEffect(() => {
    loadUser().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mapeo de rutas del dashboard a nombres de módulos
  const getModuleNameFromRoute = (pathname: string): string | null => {
    const cleanPath = pathname.replace('/dashboard', '');

    if (cleanPath === '/' || cleanPath === '') return 'Dashboard';

    if (cleanPath.startsWith('/miembros/usuarios')) return 'Usuarios';
    if (cleanPath.startsWith('/miembros/membresias')) return 'Membresías';
    if (cleanPath.startsWith('/seguridad/roles')) return 'Roles';
    if (cleanPath.startsWith('/seguridad/modulos')) return 'Módulos';
    if (cleanPath.startsWith('/seguridad/permisos')) return 'Permisos';
    if (cleanPath.startsWith('/iglesia/ministerios')) return 'Ministerios';
    if (cleanPath.startsWith('/iglesia/contactos')) return 'Contactos';
    if (cleanPath.startsWith('/iglesia/programacion-semanal')) return 'Programación Semanal';
    if (cleanPath.startsWith('/iglesia/chat')) return 'Mensajes';
    if (cleanPath.startsWith('/miembro')) return 'Mi Perfil';
    if (cleanPath.startsWith('/eventos')) return 'Eventos';
    if (cleanPath.startsWith('/mayordomia')) return 'Mayordomía';
    if (cleanPath.startsWith('/config/contenido-publico')) return 'Contenido Público';

    return null;
  };

  const moduleName = getModuleNameFromRoute(location.pathname);

  // Si no hay módulo mapeado para esta ruta, permitir acceso
  if (!moduleName) return <>{children}</>;

  // Mientras se hidrata la sesión (usuario aún null en el store), esperar en
  // lugar de negar: evita la redirección falsa al dashboard al recargar la página.
  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-sm text-gray-500">Cargando...</div>
      </div>
    );
  }

  // Verificar permiso 'ver' para el módulo
  const hasAccess = hasPermission(moduleName, 'ver');

  if (!hasAccess) {
    console.log(`🚫 Access denied to ${moduleName}`);
    // Si no tiene acceso al Dashboard, verificar si puede ir a Mi Perfil
    if (moduleName === 'Dashboard') {
      const fallback = getInitialRoute();
      return <Navigate to={fallback} replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <Routes>
          {/* Rutas públicas protegidas */}
          <Route path="/login" element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          } />
          
          {/* Rutas privadas protegidas */}
          <Route path="/dashboard/*" element={
            <PrivateRoute>
              <DashboardLayout>
                <Routes>
                  <Route path="/" element={<PermissionRoute><DashboardPage /></PermissionRoute>} />
                  <Route path="miembros/membresias" element={<PermissionRoute><MembresíasPage /></PermissionRoute>} />
                  <Route path="miembros/usuarios" element={<PermissionRoute><UsuariosPage /></PermissionRoute>} />
                  <Route path="seguridad/roles" element={<PermissionRoute><RolesPage /></PermissionRoute>} />
                  <Route path="seguridad/modulos" element={<PermissionRoute><ModulesPage /></PermissionRoute>} />
                  <Route path="seguridad/permisos" element={<PermissionRoute><PermissionsPage /></PermissionRoute>} />
                  <Route path="iglesia/ministerios" element={<PermissionRoute><MinisteriosPage /></PermissionRoute>} />
                  <Route path="iglesia/contactos" element={<PermissionRoute><DashboardContactosPage /></PermissionRoute>} />
                  <Route path="iglesia/programacion-semanal" element={<PermissionRoute><ProgramacionSemanalPage /></PermissionRoute>} />
                  <Route path="eventos" element={<PermissionRoute><DashboardEventosPage /></PermissionRoute>} />
                  <Route path="iglesia/chat" element={<PermissionRoute><ChatPage /></PermissionRoute>} />
                  <Route path="miembro" element={<PermissionRoute><MemberDashboardPage /></PermissionRoute>} />
                  <Route path="mayordomia" element={<PermissionRoute><Suspense fallback={<div className="p-8 text-center">Cargando...</div>}><MayordomiaPage /></Suspense></PermissionRoute>} />
                  <Route path="config/contenido-publico" element={<PermissionRoute><ContenidoPublicoPage /></PermissionRoute>} />
                </Routes>
              </DashboardLayout>
            </PrivateRoute>
          } />
          
          {/* Rutas públicas con layout protegidas */}
          <Route path="/*" element={
            <PublicRoute>
              <Layout>
                <Routes>
                  <Route path="/" element={<InicioPage />} />
                  <Route path="/nosotros" element={<NosotrosPage />} />
                  <Route path="/eventos" element={<EventosPage />} />
                  <Route path="/iglesia" element={<IglesiaPage />} />
                  <Route path="/contacto" element={<ContactoPage />} />
                  <Route path="/donaciones" element={<DonacionesPage />} />
                  <Route path="/fundacion" element={<FundacionPage />} />
                  {/* Aquí se agregarán más rutas */}
                </Routes>
              </Layout>
            </PublicRoute>
          } />
        </Routes>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
